import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Configuración del editor visual: barra de herramientas y formatos permitidos
const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link'],
    ['clean'],
  ],
};
const QUILL_FORMATS = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'list', 'bullet',
  'align',
  'link',
];

export default function AdminEnviarEmails() {
  const [activeTab, setActiveTab] = useState('redactar'); // 'redactar' | 'historial'
  const [destinos, setDestinos] = useState({ usuarios: [], clases: [], from_options: ['noreply'], dominio: 'ibaconnect.info', es_profesor: false, tipos_permitidos: ['usuario','rol','clase','todos','externo'] });
  const [loadingDestinos, setLoadingDestinos] = useState(true);

  const [form, setForm] = useState({
    asunto: '',
    mensaje: '',
    tipo_destinatario: 'usuario', // 'usuario' | 'rol' | 'clase' | 'todos' | 'externo'
    destinatarios_ids: [],
    emails_externos_raw: '', // string con emails separados por coma, ; o salto de línea
    rol: 'estudiante',
    clase_id: '',
    from_local_part: 'noreply',
    incluir_inactivos: false,
  });
  const [busquedaUsuario, setBusquedaUsuario] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState('');

  const [historial, setHistorial] = useState([]);
  const [historialLoading, setHistorialLoading] = useState(false);
  const [detalle, setDetalle] = useState(null);

  useEffect(() => {
    cargarDestinatarios();
  }, []);

  useEffect(() => {
    if (activeTab === 'historial') cargarHistorial();
  }, [activeTab]);

  const tokenHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const cargarDestinatarios = async () => {
    try {
      setLoadingDestinos(true);
      const r = await axios.get(`${API}/admin/emails/destinatarios`, { headers: tokenHeader() });
      setDestinos(r.data);
      // Si es profesor, forzar from_local_part al único permitido
      // y forzar tipo_destinatario a uno permitido si el actual no lo está.
      if (r.data?.es_profesor) {
        setForm(f => ({
          ...f,
          from_local_part: (r.data.from_options && r.data.from_options[0]) || 'profesor',
          tipo_destinatario: (r.data.tipos_permitidos || []).includes(f.tipo_destinatario) ? f.tipo_destinatario : 'usuario',
        }));
      }
    } catch (e) {
      setError('Error al cargar destinatarios: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoadingDestinos(false);
    }
  };

  const cargarHistorial = async () => {
    try {
      setHistorialLoading(true);
      const r = await axios.get(`${API}/admin/emails/historial`, { headers: tokenHeader() });
      setHistorial(r.data.items || []);
    } catch (e) {
      setError('Error al cargar historial: ' + (e.response?.data?.detail || e.message));
    } finally {
      setHistorialLoading(false);
    }
  };

  const verDetalle = async (envioId) => {
    try {
      const r = await axios.get(`${API}/admin/emails/historial/${envioId}`, { headers: tokenHeader() });
      setDetalle(r.data);
    } catch (e) {
      setError('Error al cargar detalle: ' + (e.response?.data?.detail || e.message));
    }
  };

  const usuariosFiltrados = useMemo(() => {
    const q = busquedaUsuario.trim().toLowerCase();
    if (!q) return destinos.usuarios;
    return destinos.usuarios.filter(u =>
      (u.nombre_completo || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.rol || '').toLowerCase().includes(q)
    );
  }, [destinos.usuarios, busquedaUsuario]);

  const totalEstimado = useMemo(() => {
    if (form.tipo_destinatario === 'usuario') return form.destinatarios_ids.length;
    if (form.tipo_destinatario === 'externo') {
      return form.emails_externos_raw
        .split(/[,;\n]/)
        .map(s => s.trim())
        .filter(Boolean).length;
    }
    if (form.tipo_destinatario === 'rol') {
      return destinos.usuarios.filter(u => u.rol === form.rol && (form.incluir_inactivos || ['aprobado', 'activo'].includes(u.estado))).length;
    }
    if (form.tipo_destinatario === 'clase') {
      const c = destinos.clases.find(c => c.id === form.clase_id);
      return c ? c.total_inscritos : 0;
    }
    if (form.tipo_destinatario === 'todos') {
      return destinos.usuarios.filter(u => form.incluir_inactivos || ['aprobado', 'activo'].includes(u.estado)).length;
    }
    return 0;
  }, [form, destinos]);

  const toggleUsuario = (uid) => {
    setForm(f => {
      const exists = f.destinatarios_ids.includes(uid);
      return { ...f, destinatarios_ids: exists ? f.destinatarios_ids.filter(x => x !== uid) : [...f.destinatarios_ids, uid] };
    });
  };

  const seleccionarTodosVisibles = () => {
    setForm(f => ({ ...f, destinatarios_ids: usuariosFiltrados.map(u => u.id) }));
  };

  const limpiarSeleccion = () => {
    setForm(f => ({ ...f, destinatarios_ids: [] }));
  };

  // Helper: detecta si el HTML del editor está realmente vacío (Quill genera <p><br></p> cuando está vacío)
  const isMensajeVacio = (html) => {
    if (!html) return true;
    const stripped = html.replace(/<(.|\n)*?>/g, '').replace(/&nbsp;/g, ' ').trim();
    return stripped.length === 0;
  };

  const enviar = async () => {
    setError('');
    setResultado(null);
    if (!form.asunto.trim()) { setError('Falta el asunto'); return; }
    if (isMensajeVacio(form.mensaje)) { setError('Falta el mensaje'); return; }
    if (totalEstimado === 0) { setError('No hay destinatarios seleccionados'); return; }

    if (!window.confirm(`¿Confirmás enviar este email a ${totalEstimado} destinatario(s) desde ${form.from_local_part}@${destinos.dominio}?`)) return;

    try {
      setEnviando(true);
      const emailsExternos = form.tipo_destinatario === 'externo'
        ? form.emails_externos_raw.split(/[,;\n]/).map(s => s.trim()).filter(Boolean)
        : null;
      const payload = {
        asunto: form.asunto,
        mensaje: form.mensaje,
        tipo_destinatario: form.tipo_destinatario,
        destinatarios_ids: form.tipo_destinatario === 'usuario' ? form.destinatarios_ids : null,
        emails_externos: emailsExternos,
        rol: form.tipo_destinatario === 'rol' ? form.rol : null,
        clase_id: form.tipo_destinatario === 'clase' ? form.clase_id : null,
        from_local_part: form.from_local_part,
        incluir_inactivos: form.incluir_inactivos,
      };
      const r = await axios.post(`${API}/admin/emails/enviar`, payload, { headers: tokenHeader() });
      setResultado(r.data);
      // Limpiar form parcial (mantener tipo destinatario, limpiar asunto/mensaje)
      setForm(f => ({ ...f, asunto: '', mensaje: '', destinatarios_ids: [], emails_externos_raw: '' }));
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-4 md:mb-6">
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
            <i className="fas fa-envelope-open-text text-purple-600" />
            Enviar Email
          </h1>
          <p className="text-sm md:text-base text-gray-600">
            {destinos.es_profesor
              ? <>Enviá emails personalizados desde <strong>{form.from_local_part}@{destinos.dominio}</strong> a tus alumnos.</>
              : <>Enviá emails personalizados desde <strong>@{destinos.dominio}</strong> a usuarios, clases o roles específicos.</>
            }
          </p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-4 md:mb-6">
          <div className="flex border-b">
            <button
              data-testid="tab-redactar"
              onClick={() => setActiveTab('redactar')}
              className={`flex-1 py-3 px-4 text-sm font-medium ${activeTab === 'redactar' ? 'border-b-2 border-purple-600 text-purple-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <i className="fas fa-pen-to-square mr-2" />Redactar
            </button>
            <button
              data-testid="tab-historial"
              onClick={() => setActiveTab('historial')}
              className={`flex-1 py-3 px-4 text-sm font-medium ${activeTab === 'historial' ? 'border-b-2 border-purple-600 text-purple-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <i className="fas fa-clock-rotate-left mr-2" />Historial
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-lg mb-4 text-sm">
            <i className="fas fa-circle-exclamation mr-2" />{error}
          </div>
        )}

        {resultado && (
          <div className="bg-green-50 border border-green-200 text-green-800 p-4 rounded-lg mb-4">
            <p className="font-semibold mb-1"><i className="fas fa-circle-check mr-2" />Envío completado</p>
            <p className="text-sm">
              Enviado a <strong>{resultado.exitosos}</strong> de <strong>{resultado.total_destinatarios}</strong> destinatarios desde <code className="bg-white px-1 rounded">{resultado.from_email}</code>.
              {resultado.fallidos > 0 && (<span className="text-orange-700 ml-2">{resultado.fallidos} fallidos.</span>)}
            </p>
          </div>
        )}

        {activeTab === 'redactar' && (
          <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 space-y-5">
            {/* Remitente */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-user-tie mr-2 text-purple-600" />Remitente (desde)
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  data-testid="select-from-local-part"
                  value={form.from_local_part}
                  onChange={(e) => setForm({ ...form, from_local_part: e.target.value })}
                  disabled={!!destinos.es_profesor}
                  className={`px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 ${destinos.es_profesor ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                >
                  {destinos.from_options.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                <span className="text-gray-500">@{destinos.dominio}</span>
                {destinos.es_profesor && (
                  <span className="text-xs text-purple-600 ml-1"><i className="fas fa-lock mr-1" />Tu dirección personal asignada</span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">Las respuestas llegarán a soporteibauruguay@gmail.com.</p>
            </div>

            {/* Tipo destinatario */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-users mr-2 text-purple-600" />¿A quién enviar?
              </label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {[
                  { value: 'usuario', label: 'Usuarios elegidos', icon: 'fa-user-check' },
                  { value: 'rol', label: 'Por rol', icon: 'fa-user-tag' },
                  { value: 'clase', label: 'Una clase', icon: 'fa-chalkboard-user' },
                  { value: 'todos', label: 'Todos', icon: 'fa-globe' },
                  { value: 'externo', label: 'Email externo', icon: 'fa-paper-plane' },
                ].filter(opt => (destinos.tipos_permitidos || []).includes(opt.value)).map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    data-testid={`btn-tipo-${opt.value}`}
                    onClick={() => setForm({ ...form, tipo_destinatario: opt.value, destinatarios_ids: [] })}
                    className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                      form.tipo_destinatario === opt.value
                        ? 'border-purple-600 bg-purple-50 text-purple-800'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    <i className={`fas ${opt.icon} mr-1`} />
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Selectores específicos por tipo */}
            {form.tipo_destinatario === 'rol' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
                <select
                  data-testid="select-rol"
                  value={form.rol}
                  onChange={(e) => setForm({ ...form, rol: e.target.value })}
                  className="w-full md:w-72 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="estudiante">Estudiantes</option>
                  <option value="profesor">Profesores</option>
                  <option value="pastor">Pastores</option>
                  <option value="graduado">Graduados</option>
                  <option value="admin">Administradores</option>
                </select>
              </div>
            )}

            {form.tipo_destinatario === 'clase' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Clase</label>
                <select
                  data-testid="select-clase"
                  value={form.clase_id}
                  onChange={(e) => setForm({ ...form, clase_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">-- Selecciona una clase --</option>
                  {destinos.clases.map(c => (
                    <option key={c.id} value={c.id}>{c.nombre || 'Sin nombre'} ({c.total_inscritos} inscritos)</option>
                  ))}
                </select>
              </div>
            )}

            {form.tipo_destinatario === 'usuario' && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                  <input
                    type="text"
                    data-testid="input-buscar-usuario"
                    placeholder="🔍 Buscar por nombre, email o rol..."
                    value={busquedaUsuario}
                    onChange={(e) => setBusquedaUsuario(e.target.value)}
                    className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <div className="flex gap-1">
                    <button type="button" onClick={seleccionarTodosVisibles} className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200">
                      Todos visibles
                    </button>
                    <button type="button" onClick={limpiarSeleccion} className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300">
                      Limpiar
                    </button>
                  </div>
                </div>
                <div className="max-h-64 overflow-y-auto border border-gray-200 rounded bg-white">
                  {loadingDestinos ? (
                    <p className="p-3 text-sm text-gray-500">Cargando usuarios...</p>
                  ) : usuariosFiltrados.length === 0 ? (
                    <p className="p-3 text-sm text-gray-500">Sin resultados.</p>
                  ) : (
                    usuariosFiltrados.map(u => (
                      <label key={u.id} className="flex items-center p-2 hover:bg-purple-50 cursor-pointer border-b border-gray-100">
                        <input
                          type="checkbox"
                          data-testid={`check-usuario-${u.id}`}
                          checked={form.destinatarios_ids.includes(u.id)}
                          onChange={() => toggleUsuario(u.id)}
                          className="mr-3"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">{u.nombre_completo}</p>
                          <p className="text-xs text-gray-500 truncate">{u.email} · <span className="capitalize">{u.rol}</span> · {u.estado}</p>
                        </div>
                      </label>
                    ))
                  )}
                </div>
                <p className="text-xs text-gray-600 mt-2">{form.destinatarios_ids.length} seleccionado(s) de {usuariosFiltrados.length} visibles</p>
              </div>
            )}

            {form.tipo_destinatario === 'externo' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email(s) externo(s)
                </label>
                <textarea
                  data-testid="textarea-emails-externos"
                  value={form.emails_externos_raw}
                  onChange={(e) => setForm({ ...form, emails_externos_raw: e.target.value })}
                  placeholder="ejemplo@gmail.com, otro@hotmail.com&#10;tercero@yahoo.com"
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 font-mono"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Separá múltiples emails con coma <code>,</code> punto y coma <code>;</code> o salto de línea. Útil para invitados, pastores externos, ex-alumnos, contactos puntuales.
                </p>
              </div>
            )}

            {(form.tipo_destinatario === 'rol' || form.tipo_destinatario === 'todos') && (
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  data-testid="check-incluir-inactivos"
                  checked={form.incluir_inactivos}
                  onChange={(e) => setForm({ ...form, incluir_inactivos: e.target.checked })}
                />
                Incluir usuarios inactivos/pendientes
              </label>
            )}

            {/* Asunto */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-tag mr-2 text-purple-600" />Asunto
              </label>
              <input
                type="text"
                data-testid="input-asunto"
                value={form.asunto}
                onChange={(e) => setForm({ ...form, asunto: e.target.value })}
                placeholder="Ej: Recordatorio reunión del viernes"
                maxLength={150}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
              />
              <p className="text-xs text-gray-500 mt-1">{form.asunto.length}/150</p>
            </div>

            {/* Mensaje */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-pen-nib mr-2 text-purple-600" />Mensaje
              </label>
              <div className="border border-gray-300 rounded-lg overflow-hidden bg-white" data-testid="editor-mensaje-wrapper">
                <ReactQuill
                  theme="snow"
                  value={form.mensaje}
                  onChange={(html) => setForm({ ...form, mensaje: html })}
                  modules={QUILL_MODULES}
                  formats={QUILL_FORMATS}
                  placeholder="Escribí tu mensaje con formato (negrita, color, links, listas...). Todo el formato se conserva al ser recibido."
                  style={{ minHeight: 220 }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                💡 Usá la barra para aplicar <strong>negrita</strong>, <em>cursiva</em>, color, listas, alineación o links.
                El email mantiene el formato cuando lo abren tus destinatarios.
              </p>
            </div>

            {/* Resumen */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <p className="text-sm text-purple-900">
                <i className="fas fa-paper-plane mr-2" />
                Listo para enviar a <strong>{totalEstimado}</strong> destinatario(s) desde <code className="bg-white px-1 rounded">{form.from_local_part}@{destinos.dominio}</code>
              </p>
            </div>

            {/* Botón enviar */}
            <button
              type="button"
              data-testid="btn-enviar-email"
              onClick={enviar}
              disabled={enviando || totalEstimado === 0 || !form.asunto.trim() || isMensajeVacio(form.mensaje)}
              className={`w-full py-3 px-4 rounded-lg font-medium text-sm md:text-base transition-colors ${
                enviando || totalEstimado === 0 || !form.asunto.trim() || isMensajeVacio(form.mensaje)
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
            >
              {enviando ? (
                <span><i className="fas fa-spinner fa-spin mr-2" />Enviando...</span>
              ) : (
                <span><i className="fas fa-paper-plane mr-2" />Enviar a {totalEstimado} destinatario(s)</span>
              )}
            </button>
          </div>
        )}

        {activeTab === 'historial' && (
          <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
            {historialLoading ? (
              <p className="text-center text-gray-500 py-8"><i className="fas fa-spinner fa-spin mr-2" />Cargando...</p>
            ) : historial.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No hay envíos registrados aún.</p>
            ) : (
              <div className="space-y-2">
                {historial.map((h) => (
                  <div key={h.id} data-testid={`historial-item-${h.id}`} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 cursor-pointer" onClick={() => verDetalle(h.id)}>
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">{h.asunto}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          De <code className="bg-gray-100 px-1 rounded">{h.from_email}</code> ·
                          {' '}{new Date(h.fecha_envio).toLocaleString('es-UY')} ·
                          {' '}{h.admin_email}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${
                          h.estado === 'completado' ? 'bg-green-100 text-green-700' :
                          h.estado === 'parcial' ? 'bg-orange-100 text-orange-700' :
                          h.estado === 'fallido' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {h.exitosos}/{h.total_destinatarios}
                        </span>
                        <p className="text-xs text-gray-500 mt-1 capitalize">{h.estado}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Modal detalle */}
        {detalle && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setDetalle(null)}>
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-4 md:p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-bold text-gray-800">📧 Detalle de envío</h2>
                <button onClick={() => setDetalle(null)} className="text-gray-500 hover:text-gray-700">
                  <i className="fas fa-times text-lg" />
                </button>
              </div>
              <div className="space-y-3 text-sm">
                <p><strong>Asunto:</strong> {detalle.asunto}</p>
                <p><strong>De:</strong> <code className="bg-gray-100 px-1 rounded">{detalle.from_email}</code></p>
                <p><strong>Por:</strong> {detalle.admin_email}</p>
                <p><strong>Fecha:</strong> {new Date(detalle.fecha_envio).toLocaleString('es-UY')}</p>
                <p><strong>Resultado:</strong> ✅ {detalle.exitosos} exitosos · ❌ {detalle.fallidos} fallidos · Total: {detalle.total_destinatarios}</p>
                <div>
                  <p className="font-semibold mb-1">Mensaje:</p>
                  <div className="bg-gray-50 border border-gray-200 rounded p-3 max-h-60 overflow-y-auto whitespace-pre-wrap text-xs">{detalle.mensaje_preview}</div>
                </div>
                {detalle.errores && detalle.errores.length > 0 && (
                  <div>
                    <p className="font-semibold text-red-700 mb-1">Errores ({detalle.errores.length}):</p>
                    <div className="bg-red-50 border border-red-200 rounded p-2 max-h-40 overflow-y-auto text-xs space-y-1">
                      {detalle.errores.map((er, i) => (
                        <div key={i}><strong>{er.email}:</strong> {er.error}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
